import { CollectionSummary } from "@/types/collection"
import { InspirationSummary } from "@/types/inspiration"
import { PublicUser, PublicUserProfile } from "@/types/user"
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"

import { RootState } from "@/lib/storage/redux-store"

export const PublicProfileGuidesTab = "guides"
export const PublicProfileCollectionsTab = "collections"
export const PublicProfileReviewsTab = "reviews"
export const PublicProfileDefaultTab = PublicProfileGuidesTab

export const PublicProfileTabTypeValues = [
  PublicProfileGuidesTab,
  PublicProfileCollectionsTab,
  PublicProfileReviewsTab,
]

export type PublicProfileTabType =
  | typeof PublicProfileGuidesTab
  | typeof PublicProfileCollectionsTab
  | typeof PublicProfileReviewsTab

export interface PublicProfileSliceState {
  publicUser?: PublicUser
  publicUserProfile?: PublicUserProfile
  publicTab: PublicProfileTabType
  loadingPublicGuides: boolean
  loadingPublicCollections: boolean
  publicGuides: InspirationSummary[]
  publicCollections: CollectionSummary[]
  publicGuidesCount: number
  publicCollectionsCount: number
  publicCurrInspirationPage: number
  publicMaxInspirationPage: number
  publicCurrCollectionsPage: number
  publicMaxCollectionsPage: number
}

export const publicProfileSliceInitialState: PublicProfileSliceState = {
  publicUser: undefined,
  publicUserProfile: undefined,
  publicTab: PublicProfileDefaultTab,
  loadingPublicGuides: false,
  loadingPublicCollections: false,
  publicGuides: [],
  publicCollections: [],
  publicGuidesCount: 0,
  publicCollectionsCount: 0,
  publicCurrInspirationPage: 1,
  publicMaxInspirationPage: 1,
  publicCurrCollectionsPage: 1,
  publicMaxCollectionsPage: 1,
}

const publicProfileSlice = createSlice({
  name: "publicProfileSlice",
  initialState: publicProfileSliceInitialState,
  reducers: {
    setPublicUser: (state, action: PayloadAction<PublicUser>) => {
      state.publicUser = action.payload
    },
    setPublicUserProfile: (state, action: PayloadAction<PublicUserProfile>) => {
      state.publicUserProfile = action.payload
    },
    setPublicTab: (state, action: PayloadAction<PublicProfileTabType>) => {
      state.publicTab = action.payload
    },
    setPublicLoadingGuides: (state, action: PayloadAction<boolean>) => {
      state.loadingPublicGuides = action.payload
    },
    setPublicLoadingCollections: (state, action: PayloadAction<boolean>) => {
      state.loadingPublicCollections = action.payload
    },
    setPublicGuides: (state, action: PayloadAction<InspirationSummary[]>) => {
      state.publicGuides = action.payload
    },
    setPublicCollections: (
      state,
      action: PayloadAction<CollectionSummary[]>
    ) => {
      state.publicCollections = action.payload
    },
    setPublicGuidesCount: (state, action: PayloadAction<number>) => {
      state.publicGuidesCount = action.payload
    },
    setPublicCollectionsCount: (state, action: PayloadAction<number>) => {
      state.publicCollectionsCount = action.payload
    },
    setPublicCurrInspirationPage: (state, action: PayloadAction<number>) => {
      state.publicCurrInspirationPage = action.payload
    },
    setPublicMaxInspirationPage: (state, action: PayloadAction<number>) => {
      state.publicMaxInspirationPage = action.payload
    },
    setPublicCurrCollectionsPage: (state, action: PayloadAction<number>) => {
      state.publicCurrCollectionsPage = action.payload
    },
    setPublicMaxCollectionsPage: (state, action: PayloadAction<number>) => {
      state.publicMaxCollectionsPage = action.payload
    },
    resetPublicProfile: () => publicProfileSliceInitialState,
    addPublicGuides: (state, action: PayloadAction<InspirationSummary[]>) => {
      state.publicGuides = [...state.publicGuides, ...action.payload]
    },
    addPublicCollections: (
      state,
      action: PayloadAction<CollectionSummary[]>
    ) => {
      state.publicCollections = [...state.publicCollections, ...action.payload]
    },
  },
})

const selectSliceState = (state: RootState) => state.publicProfileSlice

export const selectPublicUser = createSelector(
  [selectSliceState],
  (state) => state.publicUser
)

export const selectPublicUserProfile = createSelector(
  [selectSliceState],
  (state) => state.publicUserProfile
)

export const selectPublicLoadingGuides = createSelector(
  [selectSliceState],
  (state) => state.loadingPublicGuides
)

export const selectPublicLoadingCollections = createSelector(
  [selectSliceState],
  (state) => state.loadingPublicCollections
)

export const selectPublicGuides = createSelector(
  [selectSliceState],
  (state) => state.publicGuides
)

export const selectPublicCollections = createSelector(
  [selectSliceState],
  (state) => state.publicCollections
)

export const selectPublicCurrInspirationPage = createSelector(
  [selectSliceState],
  (state) => state.publicCurrInspirationPage
)

export const selectPublicMaxInspirationPage = createSelector(
  [selectSliceState],
  (state) => state.publicMaxInspirationPage
)

export const selectPublicTab = createSelector(
  [selectSliceState],
  (state) => state.publicTab
)

export const selectPublicGuidesCount = createSelector(
  [selectSliceState],
  (state) => state.publicGuidesCount
)

export const selectPublicCollectionsCount = createSelector(
  [selectSliceState],
  (state) => state.publicCollectionsCount
)

export const selectPublicCurrCollectionsPage = createSelector(
  [selectSliceState],
  (state) => state.publicCurrCollectionsPage
)

export const selectPublicMaxCollectionsPage = createSelector(
  [selectSliceState],
  (state) => state.publicMaxCollectionsPage
)

export const {
  setPublicUser,
  setPublicUserProfile,
  setPublicLoadingGuides,
  setPublicLoadingCollections,
  setPublicGuides,
  setPublicCurrInspirationPage,
  setPublicMaxInspirationPage,
  setPublicCurrCollectionsPage,
  setPublicMaxCollectionsPage,
  setPublicCollections,
  setPublicTab,
  setPublicGuidesCount,
  setPublicCollectionsCount,
  resetPublicProfile,
  addPublicGuides,
  addPublicCollections,
} = publicProfileSlice.actions

export default publicProfileSlice
